/**
 * Client-side image helpers.
 * - loadImage: HTMLImageElement from a File/Blob.
 * - cropAndResize: crop by rect (in px), then downscale so max dim ≤ MAX_DIM.
 * - resizeOnly: downscale only (no crop).
 */
export const MAX_DIM = 1024;

export function loadImage(fileOrUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    if (typeof fileOrUrl === "string") {
      img.src = fileOrUrl;
    } else {
      const url = URL.createObjectURL(fileOrUrl);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.src = url;
    }
  });
}

function chooseOutputType(sourceType) {
  if (!sourceType) return "image/jpeg";
  if (sourceType === "image/png") return "image/png";
  if (sourceType === "image/webp") return "image/webp";
  return "image/jpeg";
}

/**
 * @param {File|Blob} file  Original image file
 * @param {{x:number,y:number,width:number,height:number}|null} cropRect
 *        Pixel rect in the original image; null = use full image.
 * @param {number} maxDim   Max output side length (default 1024).
 * @returns {Promise<Blob>} Processed image blob.
 */
export async function cropAndResize(file, cropRect, maxDim = MAX_DIM) {
  const img = await loadImage(file);
  const src = cropRect || { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };

  const ratio = Math.min(1, maxDim / Math.max(src.width, src.height));
  const outW = Math.max(1, Math.round(src.width * ratio));
  const outH = Math.max(1, Math.round(src.height * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, src.x, src.y, src.width, src.height, 0, 0, outW, outH);

  const outType = chooseOutputType(file.type);
  const quality = outType === "image/jpeg" ? 0.9 : undefined;

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas encode failed"))),
      outType,
      quality
    );
  });
}

export async function resizeOnly(file, maxDim = MAX_DIM) {
  return cropAndResize(file, null, maxDim);
}
